/**
 * VirtualClockControl
 * ------------------------------------------------------------
 * Compact UI control for the virtual clock: shows the simulated
 * time and lets the user pick speed (Pause / 1x / 10x / 60x / 600x)
 * and reset to shift start.
 *
 * Designed to live in TopHeader or ContextBar.
 */
import { Pause, Play, RotateCcw, FastForward } from 'lucide-react';
import { useVirtualClock, type SimSpeed } from '@/stores/useVirtualClock';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const SPEEDS: { value: SimSpeed; label: string }[] = [
  { value: 0, label: 'Pausa' },
  { value: 1, label: '1× (real)' },
  { value: 10, label: '10×' },
  { value: 60, label: '60× (1 min/s)' },
  { value: 600, label: '600× (timelapse)' },
];

function formatTime(d: Date): string {
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function VirtualClockControl() {
  const virtualTime = useVirtualClock((s) => s.virtualTime);
  const speed = useVirtualClock((s) => s.speed);
  const setSpeed = useVirtualClock((s) => s.setSpeed);
  const resetToShiftStart = useVirtualClock((s) => s.resetToShiftStart);

  const isPaused = speed === 0;
  const currentLabel = SPEEDS.find((s) => s.value === speed)?.label ?? `${speed}×`;

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1 rounded-md border bg-muted/30 px-2 py-1 text-xs">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setSpeed(isPaused ? 1 : 0)}
            >
              {isPaused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{isPaused ? 'Reanudar simulación' : 'Pausar simulación'}</TooltipContent>
        </Tooltip>

        <span className="font-mono font-semibold tabular-nums text-foreground">
          {formatTime(virtualTime)}
        </span>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 px-1.5 gap-1 text-xs">
              <FastForward className="h-3 w-3" />
              {currentLabel}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {SPEEDS.map((s) => (
              <DropdownMenuItem
                key={s.value}
                onClick={() => setSpeed(s.value)}
                className={speed === s.value ? 'bg-accent font-semibold' : ''}
              >
                {s.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={resetToShiftStart}
            >
              <RotateCcw className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Reiniciar al inicio del turno (06:00)</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
